import { Link } from 'react-router-dom';
import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/comics';

function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      setStatusMsg('留言已发送，我们会尽快回复您！');
      form.reset();
      setSubmitting(false);
      return;
    }

    try {
      const searchParams = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          searchParams.append(key, String(value));
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9otb48senbnioleslhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: searchParams.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMsg('留言已发送，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setStatus('error');
          setStatusMsg('留言发送失败，请稍后再试。');
        } else {
          setStatus('error');
          setStatusMsg(typeof serverMsg === 'string' ? serverMsg : '留言发送失败，请稍后再试。');
        }
      }
    } catch {
      setStatus('error');
      setStatusMsg('网络异常，请稍后再试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="true">
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
        <input
          type="text"
          id="contact-name"
          name="name"
          required
          className="w-full bg-background-100 border border-background-300/60 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
        <input
          type="email"
          id="contact-email"
          name="email"
          required
          className="w-full bg-background-100 border border-background-300/60 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          maxLength={500}
          required
          className="w-full bg-background-100 border border-background-300/60 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
          placeholder="请输入您的留言内容（最多500字）"
        ></textarea>
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>
      <input type="text" name="phone_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 py-3 rounded-full font-medium text-sm transition-colors whitespace-nowrap cursor-pointer"
      >
        {submitting ? '发送中...' : '发送留言'}
      </button>
      {status === 'success' && (
        <div className="flex items-center gap-2 text-sm text-accent-700 bg-accent-50 rounded-lg px-4 py-3">
          <i className="ri-checkbox-circle-line"></i>
          {statusMsg}
        </div>
      )}
      {status === 'error' && (
        <div className="flex items-center gap-2 text-sm text-primary-700 bg-primary-50 rounded-lg px-4 py-3">
          <i className="ri-error-warning-line"></i>
          {statusMsg}
        </div>
      )}
    </form>
  );
}

export default function AboutPage() {
  const testmonials = [
    {
      name: '小美',
      avatar: 'https://readdy.ai/api/search-image?query=A%20young%20Asian%20girl%20portrait%20with%20warm%20smile%2C%20soft%20natural%20lighting%2C%20simple%20clean%20white%20background%2C%20professional%20headshot%20style&width=120&height=120&seq=avatar-user-01&orientation=squarish',
      text: '韩国漫画网的更新速度真的很快，我追的几部漫画每周都能第一时间看到最新话，而且手机阅读体验特别好！',
      role: '忠实读者 · 使用2年',
    },
    {
      name: '大雄',
      avatar: 'https://readdy.ai/api/search-image?query=A%20young%20Asian%20man%20portrait%20with%20gentle%20expression%2C%20soft%20natural%20lighting%2C%20clean%20white%20background%2C%20casual%20style%20headshot&width=120&height=120&seq=avatar-user-02&orientation=squarish',
      text: '下拉式阅读设计太赞了！一路往下滑就能畅快读完一话，不用像翻页漫画那样总是要点下一页，体验流畅了很多。',
      role: 'VIP用户 · 使用1年',
    },
    {
      name: '星野',
      avatar: 'https://readdy.ai/api/search-image?query=A%20young%20Asian%20woman%20portrait%20with%20bright%20eyes%2C%20soft%20natural%20lighting%2C%20clean%20white%20background%2C%20modern%20headshot%20style&width=120&height=120&seq=avatar-user-03&orientation=squarish',
      text: '分类做得特别清晰，找漫画很方便。而且完全免费，对于学生党来说简直是最大的福音！推荐给了身边所有喜欢韩漫的朋友。',
      role: '资深漫迷 · 使用3年',
    },
  ];

  const milestones = [
    { year: '2020', title: '网站创立', desc: '韩国漫画网正式上线，致力于为中文读者提供优质韩漫阅读体验' },
    { year: '2021', title: '资源破千', desc: '漫画资源突破1000部，覆盖恋爱、奇幻、动作等全部主流类型' },
    { year: '2022', title: '百万用户', desc: '月活跃用户突破百万，成为中文圈最具影响力的韩漫平台之一' },
    { year: '2023', title: '全面升级', desc: '推出下拉式阅读模式，优化移动端体验，支持所有设备畅快阅读' },
    { year: '2024', title: '内容深化', desc: '与更多韩国漫画平台合作，资源突破万部，更新速度与韩国同步' },
    { year: '2025', title: '社区生态', desc: '建立读者社区，打造韩漫爱好者交流互动平台' },
    { year: '2026', title: '智能推荐', desc: '引入智能推荐系统，为用户提供个性化漫画推荐服务' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://www.koreancomic.cn',
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '望京SOHO T3-A座 2508室',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: siteInfo.email,
      telephone: siteInfo.phone,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: '/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
      },
    ],
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩国漫画网',
    description: siteInfo.description,
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100/50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-1.5 text-xs text-foreground-400">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </li>
                <li className="text-foreground-700 font-medium">关于我们</li>
              </ol>
            </nav>

            <div className="text-center max-w-2xl mx-auto">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于韩国漫画网
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                {siteInfo.description}
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  我们的品牌故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩国漫画网诞生于2020年，由一群热爱韩国漫画的年轻人共同创立。我们深知韩漫爱好者在寻找优质中文翻译资源时遇到的困难——资源分散、更新缓慢、阅读体验参差不齐。
                  </p>
                  <p>
                    因此，我们决定打造一个专注、专业、免费的韩国漫画在线阅读平台。从最初几十部漫画的小站点起步，到今天拥有超过10,000部漫画资源的大型平台，我们始终坚守初心：让每一位韩漫爱好者都能轻松、愉快地享受阅读。
                  </p>
                  <p>
                    我们独创的<strong>下拉式阅读模式</strong>，彻底改变了传统翻页漫画的阅读方式。所有内容在一条垂直线上排列，只需向下滑动即可实现无缝连续阅读。无论是在电脑、平板还是手机上，都能获得极致的阅读体验。
                  </p>
                  <p>
                    韩国漫画网的漫画资源覆盖<strong>恋爱、奇幻、动作、悬疑、BL、校园、搞笑、古风</strong>等八大分类，无论您喜欢什么类型的漫画，这里都能找到心仪的作品。我们与韩国多家漫画平台保持密切合作，确保更新速度与韩国保持同步。
                  </p>
                </div>
              </div>
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20Korean%20comic%20books%20and%20reading%20nook%2C%20cozy%20atmosphere%20with%20warm%20lighting%2C%20bookshelf%20with%20colorful%20manhwa%20volumes%2C%20comfortable%20reading%20chair%2C%20soft%20coral%20and%20cream%20color%20palette%2C%20minimalist%20Korean%20interior%20style%2C%20inviting%20and%20warm&width=800&height=800&seq=about-brand-img&orientation=squarish"
                  alt="韩国漫画网品牌形象"
                  className="w-full h-full object-cover"
                  title="韩国漫画网 - 韩漫大全在线阅读平台"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100/50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                发展历程
              </h2>
              <p className="text-sm text-foreground-500">从创立到今天，每一步都坚定而有力</p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300/60 transform -translate-x-1/2 hidden md:block"></div>

              <div className="space-y-8 md:space-y-12">
                {milestones.map((item, idx) => (
                  <div key={idx} className={`flex flex-col md:flex-row gap-4 md:gap-8 items-start ${
                    idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      {idx % 2 === 0 && (
                        <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                          <h4 className="font-semibold text-foreground-950 mb-1">{item.title}</h4>
                          <p className="text-sm text-foreground-500">{item.desc}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 flex items-center gap-3 md:flex-col md:gap-2">
                      <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold">
                        {item.year.slice(2)}
                      </div>
                      <span className="text-sm font-semibold text-foreground-950 md:hidden">{item.year}</span>
                    </div>

                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-left' : 'md:text-right'} md:hidden`}>
                      <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-primary-500">{item.year}</span>
                          <h4 className="font-semibold text-foreground-950">{item.title}</h4>
                        </div>
                        <p className="text-sm text-foreground-500">{item.desc}</p>
                      </div>
                    </div>

                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-left' : 'md:text-right'} hidden md:block`}>
                      {idx % 2 === 1 && (
                        <div className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                          <h4 className="font-semibold text-foreground-950 mb-1">{item.title}</h4>
                          <p className="text-sm text-foreground-500">{item.desc}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                读者口碑
              </h2>
              <p className="text-sm text-foreground-500">听听我们的读者怎么说</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testmonials.map((item, idx) => (
                <div key={idx} className="bg-background-100/50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                      <img src={item.avatar} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950">{item.name}</div>
                      <div className="text-xs text-foreground-400">{item.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.text}</p>
                  <div className="flex items-center gap-0.5 mt-4 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-1">10,000+</div>
                <div className="text-xs text-foreground-500">漫画资源</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-1">50M+</div>
                <div className="text-xs text-foreground-500">月浏览量</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-1">99.7%</div>
                <div className="text-xs text-foreground-500">服务可用率</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-1">30min</div>
                <div className="text-xs text-foreground-500">平均日阅读时长</div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100/50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mb-8 leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们联系。
                  我们会在工作日内24小时内回复您的咨询。
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950 mb-0.5">电子邮件</div>
                      <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                        {siteInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100">
                      <i className="ri-phone-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950 mb-0.5">客服电话</div>
                      <span className="text-sm text-foreground-500">{siteInfo.phone}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100">
                      <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-950 mb-0.5">办公地址</div>
                      <span className="text-sm text-foreground-500">{siteInfo.address}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-6">
                    在线留言
                  </h3>
                  <ContactForm />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
    </>
  );
}